import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UploadSimple, FileText, DownloadSimple, Trash, SignOut, CalendarCheck, FolderOpen, UsersThree, ShareNetwork, Plus, CaretLeft, Receipt } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";

const ease = [0.22, 1, 0.36, 1];

function fmtSize(b) {
  if (!b) return "—";
  const k = b / 1024;
  return k > 1024 ? `${(k / 1024).toFixed(1)} MB` : `${Math.round(k)} KB`;
}

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("documents");
  const [docs, setDocs] = useState([]);
  const [appts, setAppts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("Wills");
  const [myAppts, setMyAppts] = useState([]);
  const fileRef = useRef(null);
  const isAdmin = user?.role === "admin";

  const DOC_CATEGORIES = ["Wills", "Trusts", "Power of Attorney", "IDs & Records", "Other"];

  const [family, setFamily] = useState([]);
  const [shared, setShared] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "Spouse" });
  const [inviting, setInviting] = useState(false);
  const [owner, setOwner] = useState(null);
  const [ownerDocs, setOwnerDocs] = useState([]);
  const [ownerAppts, setOwnerAppts] = useState([]);

  const loadFamily = useCallback(async () => {
    if (isAdmin) return;
    try {
      const [f, s] = await Promise.all([api.get("/family"), api.get("/family/shared")]);
      setFamily(f.data); setShared(s.data);
    } catch { /* ignore */ }
  }, [isAdmin]);

  const loadDocs = useCallback(async () => {
    try { const { data } = await api.get("/documents"); setDocs(data); } catch { toast.error("Failed to load documents"); }
  }, []);

  const loadAppts = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/appointments"); setAppts(data); } catch { /* ignore */ }
  }, [isAdmin]);

  const loadMyAppts = useCallback(async () => {
    if (isAdmin) return;
    try { const { data } = await api.get("/my/appointments"); setMyAppts(data); } catch { /* ignore */ }
  }, [isAdmin]);

  const [txns, setTxns] = useState([]);
  const loadTxns = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/payments/transactions"); setTxns(data); } catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => { loadDocs(); loadAppts(); loadFamily(); loadMyAppts(); loadTxns(); }, [loadDocs, loadAppts, loadFamily, loadMyAppts, loadTxns]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post("/family/invite", invite);
      toast.success(`Invitation sent to ${invite.email}`);
      setInvite({ email: "", role: "Spouse" });
      loadFamily();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send invite"); }
    finally { setInviting(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/family/${id}`); setFamily((p) => p.filter((x) => x.id !== id)); toast.success("Access revoked"); }
    catch { toast.error("Could not revoke"); }
  };

  const openOwner = async (o) => {
    setOwner(o);
    try {
      const [d, a] = await Promise.all([
        api.get(`/family/shared/${o.owner_id}/documents`),
        api.get(`/family/shared/${o.owner_id}/appointments`),
      ]);
      setOwnerDocs(d.data); setOwnerAppts(a.data);
    } catch { toast.error("Could not load shared items"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      loadDocs();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = (d) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/documents/${d.id}/download?auth=${token}`, "_blank");
  };

  const remove = async (d) => {
    try { await api.delete(`/documents/${d.id}`); toast.success("Document removed"); setDocs((prev) => prev.filter((x) => x.id !== d.id)); }
    catch { toast.error("Delete failed"); }
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/appointments/${a.id}/status`, { status });
      setAppts((prev) => prev.map((x) => (x.id === a.id ? { ...x, status } : x)));
      toast.success(status === "confirmed" ? "Consultation confirmed — client notified" : "Consultation declined — client notified");
    } catch { toast.error("Could not update status"); }
  };

  const badge = { pending: "text-gold border-gold/40", confirmed: "text-green-400 border-green-400/40", declined: "text-red-400 border-red-400/40" };

  const groupDocs = (list) => DOC_CATEGORIES.map((c) => ({ category: c, items: list.filter((d) => (d.category || "Other") === c) })).filter((g) => g.items.length);

  const DocRow = ({ d, showDelete, testPrefix }) => (
    <div className="flex items-center gap-4 p-5 hover:bg-[#0f0f0f] transition-colors duration-300">
      <FileText size={26} weight="thin" className="text-gold shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-bone truncate">{d.original_filename}</div>
        <div className="text-bone/40 text-xs mt-0.5">{fmtSize(d.size)} · {new Date(d.created_at).toLocaleDateString()}</div>
      </div>
      <button onClick={() => download(d)} data-testid={`${testPrefix}download-${d.id}`} className="p-2.5 text-bone/50 hover:text-gold transition-colors duration-300" title="Download"><DownloadSimple size={20} /></button>
      {showDelete && <button onClick={() => remove(d)} data-testid={`delete-${d.id}`} className="p-2.5 text-bone/50 hover:text-red-400 transition-colors duration-300" title="Delete"><Trash size={20} /></button>}
    </div>
  );

  const FolderView = ({ list, showDelete, testPrefix = "" }) => (
    <div className="space-y-10">
      {groupDocs(list).map((g) => (
        <div key={g.category} data-testid={`folder-${g.category}`}>
          <div className="flex items-center gap-2 mb-3">
            <FolderOpen size={18} className="text-gold" />
            <span className="overline text-bone/70">{g.category}</span>
            <span className="text-bone/30 text-xs">({g.items.length})</span>
          </div>
          <div className="border border-border/50 divide-y divide-border/50">
            {g.items.map((d) => <DocRow key={d.id} d={d} showDelete={showDelete} testPrefix={testPrefix} />)}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-border/50 backdrop-blur-xl bg-ink/70 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="serif text-2xl text-bone cursor-pointer" onClick={() => navigate("/")}>Legacy<span className="text-gold">.</span></div>
          <div className="flex items-center gap-6">
            <span className="text-bone/50 text-sm hidden sm:block">{user?.name} {isAdmin && <span className="text-gold overline ml-2">Admin</span>}</span>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="portal-logout" className="inline-flex items-center gap-2 text-bone/60 hover:text-bone overline transition-colors duration-300"><SignOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <div className="overline text-gold mb-4">Client Portal</div>
          <h1 className="serif font-light text-5xl text-bone tracking-tighter mb-2">Welcome, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-bone/50 mb-12">{isAdmin ? "Manage all client documents and consultation requests." : "Securely upload and access your estate planning documents."}</p>

          <div className="flex gap-8 border-b border-border/50 mb-12">
            <button onClick={() => setTab("documents")} data-testid="tab-documents" className={`pb-4 overline flex items-center gap-2 transition-colors duration-300 ${tab === "documents" ? "text-gold border-b-2 border-gold" : "text-bone/40 hover:text-bone"}`}><FolderOpen size={18} /> Documents</button>
            {isAdmin && <button onClick={() => setTab("appointments")} data-testid="tab-appointments" className={`pb-4 overline flex items-center gap-2 transition-colors duration-300 ${tab === "appointments" ? "text-gold border-b-2 border-gold" : "text-bone/40 hover:text-bone"}`}><CalendarCheck size={18} /> Consultations</button>}
            {isAdmin && <button onClick={() => setTab("payments")} data-testid="tab-payments" className={`pb-4 overline flex items-center gap-2 transition-colors duration-300 ${tab === "payments" ? "text-gold border-b-2 border-gold" : "text-bone/40 hover:text-bone"}`}><Receipt size={18} /> Payments</button>}
            {!isAdmin && <button onClick={() => setTab("myconsults")} data-testid="tab-myconsults" className={`pb-4 overline flex items-center gap-2 transition-colors duration-300 ${tab === "myconsults" ? "text-gold border-b-2 border-gold" : "text-bone/40 hover:text-bone"}`}><CalendarCheck size={18} /> My Consultations</button>}
            {!isAdmin && <button onClick={() => setTab("family")} data-testid="tab-family" className={`pb-4 overline flex items-center gap-2 transition-colors duration-300 ${tab === "family" ? "text-gold border-b-2 border-gold" : "text-bone/40 hover:text-bone"}`}><UsersThree size={18} /> Family</button>}
            {!isAdmin && <button onClick={() => { setTab("shared"); setOwner(null); }} data-testid="tab-shared" className={`pb-4 overline flex items-center gap-2 transition-colors duration-300 ${tab === "shared" ? "text-gold border-b-2 border-gold" : "text-bone/40 hover:text-bone"}`}><ShareNetwork size={18} /> Shared With Me</button>}
          </div>

          {tab === "documents" && (
            <div>
              <div className="mb-10 flex flex-wrap items-end gap-4">
                <div>
                  <div className="overline text-bone/40 mb-2">Folder</div>
                  <select data-testid="upload-category" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} className="bg-transparent border-b border-border px-0 py-3 text-bone outline-none focus:border-gold transition-colors duration-300 appearance-none pr-8">
                    {DOC_CATEGORIES.map((c) => <option key={c} value={c} className="bg-ink">{c}</option>)}
                  </select>
                </div>
                <input ref={fileRef} type="file" onChange={onUpload} className="hidden" data-testid="file-input" />
                <button disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-button" className="group inline-flex items-center gap-3 px-8 py-4 border border-gold text-gold overline hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
                  <UploadSimple size={18} /> {uploading ? "Uploading…" : "Upload Document"}
                </button>
              </div>

              {docs.length === 0 ? (
                <div className="border border-border/50 p-16 text-center">
                  <FileText size={44} weight="thin" className="text-bone/30 mx-auto mb-4" />
                  <p className="text-bone/40">No documents yet. Choose a folder and upload your first file.</p>
                </div>
              ) : (
                <div data-testid="documents-list"><FolderView list={docs} showDelete={!isAdmin} /></div>
              )}
            </div>
          )}

          {tab === "myconsults" && !isAdmin && (
            <div>
              <p className="text-bone/50 mb-8 text-sm">Consultation requests booked with your email address.</p>
              {myAppts.length === 0 ? (
                <div className="border border-border/50 p-16 text-center text-bone/40" data-testid="my-appointments-empty">You have no consultation requests yet. Book one from the homepage.</div>
              ) : (
                <div className="border border-border/50 divide-y divide-border/50" data-testid="my-appointments-list">
                  {myAppts.map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-6">
                      <div>
                        <div className="serif text-2xl text-bone">{a.service}</div>
                        <div className="text-bone/40 text-sm mt-1">Preferred: {a.preferred_date}</div>
                      </div>
                      <span className={`overline px-3 py-1 border ${badge[a.status] || badge.pending}`} data-testid={`my-appt-status-${a.id}`}>{a.status || "pending"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "appointments" && isAdmin && (
            <div>
              {appts.length === 0 ? (
                <div className="border border-border/50 p-16 text-center text-bone/40">No consultation requests yet.</div>
              ) : (
                <div className="border border-border/50 divide-y divide-border/50" data-testid="appointments-list">
                  {appts.map((a) => (
                    <div key={a.id} className="p-6 hover:bg-[#0f0f0f] transition-colors duration-300">
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                        <div className="serif text-2xl text-bone">{a.name}</div>
                        <div className="flex items-center gap-3">
                          <span className={`overline px-3 py-1 border ${badge[a.status] || badge.pending}`} data-testid={`appt-status-${a.id}`}>{a.status || "pending"}</span>
                          <span className="overline text-bone/40">{a.service}</span>
                        </div>
                      </div>
                      <div className="text-bone/50 text-sm flex flex-wrap gap-x-6 gap-y-1">
                        <span>{a.email}</span>{a.phone && <span>{a.phone}</span>}<span>Preferred: {a.preferred_date}</span>
                      </div>
                      {a.message && <p className="text-bone/60 text-sm mt-3 leading-relaxed">{a.message}</p>}
                      {a.status !== "confirmed" && a.status !== "declined" && (
                        <div className="flex gap-3 mt-5">
                          <button onClick={() => setStatus(a, "confirmed")} data-testid={`confirm-${a.id}`} className="px-6 py-2.5 bg-gold text-ink overline hover:bg-bone transition-colors duration-300">Confirm</button>
                          <button onClick={() => setStatus(a, "declined")} data-testid={`decline-${a.id}`} className="px-6 py-2.5 border border-border text-bone/60 overline hover:border-red-400 hover:text-red-400 transition-colors duration-300">Decline</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "payments" && isAdmin && (
            <div>
              {(() => {
                const done = txns.filter((t) => t.status === "completed");
                const revenue = done.reduce((s, t) => s + parseFloat(t.amount || 0), 0);
                return (
                  <div className="grid sm:grid-cols-3 gap-px bg-border/50 mb-10">
                    <div className="bg-ink p-6"><div className="overline text-bone/40 mb-2">Total Revenue</div><div className="serif text-4xl text-gold">${revenue.toLocaleString()}</div></div>
                    <div className="bg-ink p-6"><div className="overline text-bone/40 mb-2">Completed</div><div className="serif text-4xl text-bone">{done.length}</div></div>
                    <div className="bg-ink p-6"><div className="overline text-bone/40 mb-2">All Orders</div><div className="serif text-4xl text-bone">{txns.length}</div></div>
                  </div>
                );
              })()}
              {(() => {
                const done = txns.filter((t) => t.status === "completed");
                if (done.length === 0) return null;
                const map = {};
                done.forEach((t) => {
                  const d = new Date(t.created_at);
                  const key = d.toLocaleString("en-US", { month: "short", year: "2-digit" });
                  map[key] = (map[key] || 0) + parseFloat(t.amount || 0);
                });
                const data = Object.entries(map).map(([month, revenue]) => ({ month, revenue }));
                return (
                  <div className="border border-border/50 p-6 mb-10" data-testid="revenue-chart">
                    <div className="overline text-bone/40 mb-6">Monthly Revenue</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                        <defs>
                          <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#C5A059" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#C5A059" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" vertical={false} />
                        <XAxis dataKey="month" stroke="#8a8a8a" fontSize={12} tickLine={false} axisLine={false} />
                        <YAxis stroke="#8a8a8a" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                        <Tooltip contentStyle={{ background: "#0e0e0e", border: "1px solid #2A2A2A", color: "#E8E5DF" }} formatter={(v) => [`$${v}`, "Revenue"]} />
                        <Area type="monotone" dataKey="revenue" stroke="#C5A059" strokeWidth={2} fill="url(#rev)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
              {txns.length === 0 ? (
                <div className="border border-border/50 p-16 text-center text-bone/40" data-testid="payments-empty">No transactions yet. Payments appear here once clients check out.</div>
              ) : (
                <div className="border border-border/50 divide-y divide-border/50" data-testid="payments-list">
                  {txns.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 p-5">
                      <div className="min-w-0">
                        <div className="text-bone">{t.package_name}</div>
                        <div className="text-bone/40 text-xs mt-0.5">{t.payer_email || "—"} · {new Date(t.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-5">
                        <span className="serif text-2xl text-bone">${Math.round(parseFloat(t.amount))}</span>
                        <span className={`overline px-3 py-1 border ${t.status === "completed" ? "text-green-400 border-green-400/40" : t.status === "created" ? "text-gold border-gold/40" : "text-red-400 border-red-400/40"}`}>{t.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "family" && !isAdmin && (
            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <h3 className="serif text-2xl text-bone mb-2">Invite a family member</h3>
                <p className="text-bone/50 text-sm mb-6 leading-relaxed">They'll be able to view your documents and consultation status. If they don't have an account, they'll be prompted to create one with this email.</p>
                <form onSubmit={sendInvite} data-testid="invite-form" className="space-y-6">
                  <input required type="email" data-testid="invite-email" placeholder="Family member's email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="w-full bg-transparent border-b border-border px-0 py-3 text-bone placeholder:text-bone/30 outline-none focus:border-gold transition-colors duration-300" />
                  <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="w-full bg-transparent border-b border-border px-0 py-3 text-bone outline-none focus:border-gold transition-colors duration-300 appearance-none">
                    {["Spouse", "Child", "Executor", "Other"].map((r) => <option key={r} value={r} className="bg-ink">{r}</option>)}
                  </select>
                  <button disabled={inviting} data-testid="invite-submit" className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-ink overline hover:bg-bone transition-colors duration-300 disabled:opacity-50"><Plus size={18} /> {inviting ? "Sending…" : "Send Invitation"}</button>
                </form>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <h3 className="serif text-2xl text-bone mb-6">People with access</h3>
                {family.length === 0 ? (
                  <div className="border border-border/50 p-12 text-center text-bone/40">You haven't invited anyone yet.</div>
                ) : (
                  <div className="border border-border/50 divide-y divide-border/50" data-testid="family-list">
                    {family.map((f) => (
                      <div key={f.id} className="flex items-center gap-4 p-5">
                        <UsersThree size={26} weight="thin" className="text-gold shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-bone truncate">{f.member_email}</div>
                          <div className="text-bone/40 text-xs mt-0.5">{f.role} · {f.status === "accepted" ? "Active" : "Pending signup"}</div>
                        </div>
                        <button onClick={() => revoke(f.id)} data-testid={`revoke-${f.id}`} className="p-2.5 text-bone/50 hover:text-red-400 transition-colors duration-300" title="Revoke access"><Trash size={20} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "shared" && !isAdmin && !owner && (
            <div>
              {shared.length === 0 ? (
                <div className="border border-border/50 p-16 text-center text-bone/40">No one has shared access with you yet.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-px bg-border/50" data-testid="shared-list">
                  {shared.map((s) => (
                    <button key={s.id} onClick={() => openOwner(s)} data-testid={`shared-${s.owner_id}`} className="bg-ink p-8 text-left hover:bg-[#0f0f0f] transition-colors duration-300">
                      <div className="overline text-gold mb-3">{s.role}</div>
                      <div className="serif text-2xl text-bone mb-1">{s.owner_name}</div>
                      <div className="text-bone/40 text-sm">View documents &amp; consultations →</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "shared" && !isAdmin && owner && (
            <div>
              <button onClick={() => setOwner(null)} data-testid="shared-back" className="inline-flex items-center gap-2 text-bone/50 hover:text-bone overline mb-8 transition-colors duration-300"><CaretLeft size={16} /> Back</button>
              <h3 className="serif text-3xl text-bone mb-1">{owner.owner_name}</h3>
              <p className="text-bone/40 text-sm mb-10">You have <span className="text-gold">{owner.role}</span> access · read-only</p>

              <div className="overline text-gold mb-4">Documents</div>
              {ownerDocs.length === 0 ? (
                <div className="border border-border/50 p-10 text-center text-bone/40 mb-12">No documents shared.</div>
              ) : (
                <div className="mb-12"><FolderView list={ownerDocs} showDelete={false} testPrefix="shared-" /></div>
              )}

              <div className="overline text-gold mb-4">Consultation Status</div>
              {ownerAppts.length === 0 ? (
                <div className="border border-border/50 p-10 text-center text-bone/40">No consultations found.</div>
              ) : (
                <div className="border border-border/50 divide-y divide-border/50">
                  {ownerAppts.map((a) => (
                    <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-5">
                      <div><div className="text-bone">{a.service}</div><div className="text-bone/40 text-xs mt-0.5">Preferred: {a.preferred_date}</div></div>
                      <span className={`overline px-3 py-1 border ${badge[a.status] || badge.pending}`}>{a.status || "pending"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
